import React, { useEffect, useRef, useState } from "react";
import styles from "./ProofSection.module.css";
import { useT } from "../../i18n";

const Stars = () => (
  <span className={styles.stars} role="img" aria-label="5 / 5">
    &#9733;&#9733;&#9733;&#9733;&#9733;
  </span>
);

const ProofSection = () => {
  const t = useT().proof;
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setInView(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="proof"
      ref={ref}
      className={`${styles.section} ${inView ? styles.inview : ""}`}
    >
      <div className={styles.inner}>
        <header className={styles.head}>
          <p className={styles.eyebrow}>{t.eyebrow}</p>
          <h2 className={styles.title}>{t.title}</h2>
          <p className={styles.sub}>{t.sub}</p>
        </header>

        <div className={styles.cases}>
          {t.sites.map((s, i) => (
            <article
              key={s.url}
              className={`${styles.case} ${i % 2 === 1 ? styles.caseAlt : ""}`}
            >
              {/* eslint-disable-next-line react/jsx-no-target-blank */}
              <a
                className={styles.frame}
                href={s.url}
                target="_blank"
                rel="noopener"
                aria-label={`${t.visit} ${s.name}`}
              >
                <span className={styles.bar} aria-hidden="true">
                  <span className={styles.dots}>
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className={styles.urlPill}>{s.display}</span>
                </span>
                <img
                  className={styles.shot}
                  src={s.shot}
                  alt={s.alt || s.name}
                  loading="lazy"
                  decoding="async"
                  width={s.w || 1100}
                  height={s.h || 751}
                />
              </a>

              <div className={styles.caseBody}>
                <h3 className={styles.caseName}>
                  {s.anchor ? (
                    // Followable (no nofollow) and referrer-preserving (noopener,
                    // not noreferrer) on purpose — this is the editorial SEO link.
                    // eslint-disable-next-line react/jsx-no-target-blank
                    <a
                      className={styles.caseLink}
                      href={s.url}
                      target="_blank"
                      rel="noopener"
                    >
                      {s.anchor}
                    </a>
                  ) : (
                    s.name
                  )}
                </h3>
                {s.meta && <p className={styles.caseMeta}>{s.meta}</p>}
                {(Array.isArray(s.note) ? s.note : [s.note]).map((p) => (
                  <p key={p} className={styles.caseNote}>
                    {p}
                  </p>
                ))}
                {s.quote && (
                  <>
                    <Stars />
                    <blockquote className={styles.quote}>{s.quote}</blockquote>
                    <p className={styles.cite}>
                      {s.cite} <span className={styles.via}>· {t.via}</span>
                    </p>
                  </>
                )}
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  className={styles.visit}
                  href={s.url}
                  target="_blank"
                  rel="noopener"
                >
                  {t.visit} {s.display} &rarr;
                </a>
              </div>
            </article>
          ))}
        </div>

        <div className={styles.more}>
          <p className={styles.moreHeader}>{t.moreHeader}</p>
          <div className={styles.moreGrid}>
            {t.more.map((m) => (
              <div key={m.result} className={styles.moreItem}>
                <h3 className={styles.moreResult}>{m.result}</h3>
                <p className={styles.moreDetail}>{m.detail}</p>
                {m.quote && (
                  <>
                    <Stars />
                    <blockquote className={styles.moreQuote}>{m.quote}</blockquote>
                  </>
                )}
                <p className={styles.cite}>
                  {m.cite}
                  {m.org && <span className={styles.via}> · {m.org}</span>}
                  {m.quote && <span className={styles.via}> · {t.via}</span>}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProofSection;
